import { useEffect, useMemo, useState } from 'react';
import { Box, Button, Paper, Stack, Typography } from '@mui/material';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import { useAppState } from '../../context/LoadTestContext';
import HeaderBar from '../HeaderBar';
import SaveOptionsMenu from '../SaveOptionsMenu';
import GraphCard from './GraphCard';
import ReadingsPanel from './ReadingsPanel';
import StepStrip, { durationMinutes } from './StepStrip';
import { stepLabel } from '../steps';

// The graph-first dashboard's layout, shared by the live dashboard and a past
// test opened from Historical Data: `top` (the box picker, or the past test's
// revision banner) beside the header bar, then the graph beside the readings
// column, and the load steps underneath. Everything shown comes from the
// dashboard context. `banner` (optional) is a full-width row above it all;
// `warnings` (optional, the live dashboard's) adds a Warnings tab to the graph
// card (see GraphCard).
// errorMessage replaces the graphs when the data can't be shown; resetKey (the
// box or test on screen) clears a picked step.
function DashboardBody({ top = null, banner = null, box = null, errorMessage = null, resetKey, warnings = null }) {
  const { setSaveOptionsAnchor, stepSummaryData } = useAppState();

  // null follows the newest step shown as readings arrive; a number is the
  // step (its number in step_summary) the user picked in the strip.
  const [pickedStep, setPickedStep] = useState(null);
  useEffect(() => { setPickedStep(null); }, [resetKey]);

  // A picked step that is no longer shown (unload steps hidden) falls back to
  // the newest one shown.
  const steps = Array.isArray(stepSummaryData) ? stepSummaryData : [];
  const step = steps.find((s) => s.step === pickedStep) || steps[steps.length - 1];

  const marker = useMemo(() => (step ? {
    loadKips: Number(step.end_load),
    deflectionIn: Number(step.cumulative_settlement),
    label: stepLabel(step),
  } : null), [step]);

  const selectedStep = useMemo(() => {
    if (!step) return null;
    const minutes = durationMinutes(step.step_duration_display);
    return { step: step.step, label: stepLabel(step), rateIn: minutes ? Number(step.settlement) / minutes : null };
  }, [step]);

  return (
    <Box sx={{ height: { xs: 'auto', md: '100%' }, overflowY: { md: 'auto' }, display: 'flex', flexDirection: 'column', gap: 1.5, pr: { md: 0.5 } }}>
      {banner}
      <Stack direction='row' flexWrap='wrap' alignItems='stretch' useFlexGap spacing={1.5}>
        {top}
        <Box sx={{ display: 'flex', flex: '999 1 640px', minWidth: 0 }}>
          <HeaderBar />
        </Box>
      </Stack>

      {errorMessage ? (
        <Paper sx={{ p: 4, borderRadius: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
          <Typography sx={{ textAlign: 'center' }}>{errorMessage}</Typography>
        </Paper>
      ) : (
        <>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, alignItems: 'flex-start' }}>
            <GraphCard
              marker={marker}
              selectedStep={step?.step}
              warnings={warnings}
              actions={(
                <Button
                  id='save-dashboard-button'
                  variant='contained'
                  aria-haspopup='menu'
                  startIcon={<FileDownloadOutlinedIcon />}
                  onClick={(e) => setSaveOptionsAnchor(e.currentTarget)}
                  // The graph tabs' size: it sits right after them.
                  sx={{ textTransform: 'none', height: 40, fontSize: 14, px: 1.5, whiteSpace: 'nowrap' }}
                >
                  Save Dashboard
                </Button>
              )}
            />
            <Box sx={{ flex: '1 1 300px', minWidth: { xs: '100%', sm: 280 }, maxWidth: { md: 360 } }}>
              <ReadingsPanel box={box} selectedStep={selectedStep} />
            </Box>
          </Box>
          <StepStrip selectedStep={step?.step} onSelect={setPickedStep} />
        </>
      )}
      <SaveOptionsMenu />
    </Box>
  );
}

export default DashboardBody;
